import React, { useMemo, useState } from "react";
import { PiggyBank, Target, TrendingUp, ArrowRight } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import LandingLayout, { useLandingMeta } from "@/components/landing/LandingLayout";
import { EditableSliderField } from "@/components/Simulator";
import { CALENDLY_URL } from "@/lib/siteData";

const PROFILES = [
  { id: "prudent", label: "Prudent", rate: 2, desc: "Fonds euros, livrets" },
  { id: "equilibre", label: "Équilibré", rate: 4, desc: "Mix fonds euros / unités de compte" },
  { id: "dynamique", label: "Dynamique", rate: 6, desc: "Majorité d'actions, horizon long" },
];
const META = {
  title: "Simulateur d'épargne gratuit — capital à terme et objectif | MD Courtage",
  description: "Calculez le capital de votre épargne selon vos versements, la durée et votre profil (prudent, équilibré, dynamique), ou l'effort mensuel pour atteindre votre objectif.",
  jsonLd: { "@context": "https://schema.org", "@type": "WebApplication", name: "Simulateur d'épargne MD Courtage", applicationCategory: "FinanceApplication", url: "https://mdcourtage.fr/simulateur-epargne" },
};
const eur = (v) => `${Math.round(v).toLocaleString("fr-FR")} €`;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

export function computeSavings({ initial, monthly, years, rate }) {
  const r = Math.pow(1 + rate / 100, 1 / 12) - 1;
  const fv = (m) => initial * Math.pow(1 + r, m) + (r === 0 ? monthly * m : monthly * ((Math.pow(1 + r, m) - 1) / r));
  const points = Array.from({ length: years + 1 }, (_, y) => {
    const paid = initial + monthly * 12 * y;
    const total = fv(12 * y);
    return { year: y, paid: Math.round(paid), gains: Math.round(total - paid) };
  });
  const last = points[years];
  return { points, capital: last.paid + last.gains, paid: last.paid, gains: last.gains };
}

export function monthlyForGoal({ goal, initial, years, rate }) {
  const n = years * 12;
  const r = Math.pow(1 + rate / 100, 1 / 12) - 1;
  const rest = goal - initial * Math.pow(1 + r, n);
  if (rest <= 0) return 0;
  return r === 0 ? rest / n : (rest * r) / (Math.pow(1 + r, n) - 1);
}

export default function SavingsPage() {
  useLandingMeta(META);
  const [mode, setMode] = useState("capital");
  const [initial, setInitial] = useState(5000);
  const [monthly, setMonthly] = useState(200);
  const [goal, setGoal] = useState(100000);
  const [years, setYears] = useState(15);
  const [profile, setProfile] = useState("equilibre");
  const [rate, setRate] = useState(4);

  const needed = mode === "goal" ? monthlyForGoal({ goal, initial, years, rate }) : null;
  const effMonthly = mode === "goal" ? needed : monthly;
  const res = useMemo(() => computeSavings({ initial, monthly: effMonthly, years, rate }), [initial, effMonthly, years, rate]);
  const pickProfile = (p) => { setProfile(p.id); setRate(p.rate); };

  return (
    <LandingLayout testId="savings-page" needs={["placement"]} page="simulateur-epargne">
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="text-xs uppercase tracking-[0.25em] text-md-accent font-bold mb-4">Simulateur d'épargne</div>
        <h1 data-testid="savings-h1" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-md-primary font-medium leading-tight mb-5 max-w-3xl">
          Combien rapportera <span className="italic">votre épargne ?</span>
        </h1>
        <p className="text-lg text-md-muted max-w-2xl mb-12">Estimez le capital obtenu selon vos versements et votre profil, ou l'effort mensuel nécessaire pour atteindre votre objectif.</p>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 bg-white rounded-3xl p-7 md:p-9 shadow-xl border border-md-border">
            <div className="grid grid-cols-2 gap-2 p-1 bg-md-bg rounded-full mb-8">
              <ModeBtn testId="savings-mode-capital" active={mode === "capital"} onClick={() => setMode("capital")} icon={PiggyBank} label="Mon capital à terme" />
              <ModeBtn testId="savings-mode-goal" active={mode === "goal"} onClick={() => setMode("goal")} icon={Target} label="Atteindre un objectif" />
            </div>
            <div className="space-y-7">
              {mode === "goal" && (
                <EditableSliderField testId="savings-goal" label="Objectif de capital" value={goal} unit="€" min={5000} max={1000000} step={5000} decimals={0} onChange={(v) => setGoal(clamp(v, 1000, 5000000))} />
              )}
              <EditableSliderField testId="savings-initial" label="Versement de départ" value={initial} unit="€" min={0} max={200000} step={500} decimals={0} onChange={(v) => setInitial(clamp(v, 0, 5000000))} />
              {mode === "capital" && (
                <EditableSliderField testId="savings-monthly" label="Versement mensuel" value={monthly} unit="€" min={0} max={5000} step={10} decimals={0} onChange={(v) => setMonthly(clamp(v, 0, 100000))} />
              )}
              <EditableSliderField testId="savings-years" label="Durée" value={years} unit="ans" min={1} max={40} step={1} decimals={0} onChange={(v) => setYears(clamp(Math.round(v), 1, 40))} />
              <div>
                <div className="text-sm text-md-muted mb-3">Profil de rendement</div>
                <div className="grid grid-cols-3 gap-2">
                  {PROFILES.map((p) => (
                    <button key={p.id} type="button" data-testid={`savings-profile-${p.id}`} onClick={() => pickProfile(p)}
                      className={`rounded-2xl border px-3 py-3 text-left transition-colors ${profile === p.id ? "bg-md-primary border-md-primary text-white" : "border-md-border hover:border-md-secondary text-md-primary"}`}>
                      <div className="font-semibold text-sm">{p.label}</div>
                      <div className={`text-[11px] ${profile === p.id ? "text-white/70" : "text-md-muted"}`}>{p.rate} % / an</div>
                    </button>
                  ))}
                </div>
              </div>
              <EditableSliderField testId="savings-rate" label="Rendement annuel estimé" hint={PROFILES.find((p) => p.id === profile)?.desc || "Taux personnalisé"} value={rate} unit="%" min={0} max={10} step={0.1} decimals={1}
                onChange={(v) => { setRate(clamp(parseFloat(v.toFixed(1)), 0, 15)); setProfile(""); }} />
            </div>
          </div>

          <div className="lg:col-span-7 space-y-5">
            <SavingsResults mode={mode} res={res} needed={needed} goal={goal} />
            <div className="bg-white rounded-3xl p-6 border border-md-border" data-testid="savings-chart">
              <div className="text-xs uppercase tracking-widest text-md-accent font-bold mb-4">Évolution de votre capital</div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={res.points} margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8e2d8" />
                    <XAxis dataKey="year" tickFormatter={(y) => (y === 0 ? "0" : `${y} an${y > 1 ? "s" : ""}`)} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${Math.round(v / 1000)} k€`} tick={{ fontSize: 11 }} width={60} />
                    <Tooltip formatter={(v, n) => [eur(v), n === "paid" ? "Versements" : "Intérêts"]} labelFormatter={(y) => `Année ${y}`} />
                    <Area type="monotone" dataKey="paid" stackId="1" stroke="#1F3A5F" fill="#1F3A5F" fillOpacity={0.85} />
                    <Area type="monotone" dataKey="gains" stackId="1" stroke="#D6906B" fill="#D6906B" fillOpacity={0.85} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <p data-testid="savings-disclaimer" className="text-xs text-md-muted leading-relaxed">
              * Simulation indicative avec un rendement annuel constant, brut de frais, de prélèvements sociaux et d'impôts. Les rendements
              ne sont pas garantis : les supports en unités de compte présentent un risque de perte en capital. Les performances passées
              ne préjugent pas des performances futures. Cette simulation ne constitue pas un conseil en investissement ; MD Courtage est
              conseiller en placement non indépendant, rémunéré par ses partenaires.
            </p>
          </div>
        </div>
      </main>
    </LandingLayout>
  );
}

function SavingsResults({ mode, res, needed, goal }) {
  return (
    <div className="bg-md-primary text-white rounded-3xl p-7 md:p-9 relative overflow-hidden">
      <div className="absolute -top-16 -right-16 w-56 h-56 bg-md-secondary/25 rounded-full blur-3xl" />
      <div className="relative">
        {mode === "goal" ? (
          <>
            <div className="text-[11px] uppercase tracking-[0.2em] text-md-secondary font-bold mb-2">Effort d'épargne nécessaire</div>
            <div data-testid="savings-result-needed" className="font-serif text-5xl md:text-6xl mb-1">{eur(needed)}<span className="text-2xl text-white/70"> / mois</span></div>
            <p className="text-white/70 text-sm mb-6">pour atteindre {eur(goal)}{needed === 0 ? " — votre versement de départ suffit." : "."}</p>
          </>
        ) : (
          <>
            <div className="text-[11px] uppercase tracking-[0.2em] text-md-secondary font-bold mb-2">Capital estimé à terme</div>
            <div data-testid="savings-result-capital" className="font-serif text-5xl md:text-6xl mb-6">{eur(res.capital)}</div>
          </>
        )}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Stat testId="savings-result-paid" label="Total versé" value={eur(res.paid)} />
          <Stat testId="savings-result-gains" label="Intérêts générés" value={eur(res.gains)} icon />
        </div>
        <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="savings-cta-rdv" className="inline-flex items-center gap-2 bg-md-secondary hover:bg-md-secondaryHover text-white px-6 py-3.5 rounded-full font-semibold transition-transform hover:-translate-y-0.5">
          Construire ma stratégie d'épargne <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}

const Stat = ({ label, value, testId, icon }) => (
  <div data-testid={testId} className="bg-white/10 rounded-2xl p-4">
    <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold mb-1 flex items-center gap-1">{icon && <TrendingUp className="w-3 h-3 text-md-secondary" />}{label}</div>
    <div className="font-serif text-2xl">{value}</div>
  </div>
);

const ModeBtn = ({ active, onClick, icon: Icon, label, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-full text-xs sm:text-sm font-medium transition-colors ${active ? "bg-md-primary text-white shadow-md" : "text-md-muted hover:text-md-primary"}`}>
    <Icon className="w-4 h-4 flex-shrink-0" /> {label}
  </button>
);
